import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// project imports
import useConfig from 'hooks/useConfig';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import MUIFormControl from '@mui/material/FormControl';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  useRadioGroup,
  Typography,
  Tooltip
} from '@mui/material';

// types
import { RadioInputOption } from 'models';
import { useAppSelector } from 'hooks/useAppStore';
import { selectAjaxLoader } from 'features/app/appSelectors';

interface FormControlRadioProps {
  captionLabel?: string;
  errorMessage?: string;
  helperText?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => any;
  name: string;
  value: any;
  options: RadioInputOption[];
  disabled?: boolean;
}

interface StyledGroupProps extends FormControlLabelProps {
  groupType: GroupType;
}

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean;
  disabled: boolean;
}

// constants
enum GroupType {
  RADIO = 'radio',
  TABLE = 'table'
}

const StyledTableGroup = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked, disabled }) => ({
  border: `1px solid ${
    checked
      ? disabled
        ? theme.palette.primary[200]
        : theme.palette.primary.main
      : theme.palette.grey[400]
  }`,
  marginLeft: 0,
  marginRight: 0,
  flexBasis: 1,
  flexGrow: 1,
  alignItems: 'flex-start',
  minWidth: '160px',
  padding: '14px',
  '.MuiTypography-root': {
    color: checked
      ? disabled
        ? theme.palette.primary[200]
        : theme.palette.primary.main
      : theme.palette.grey[400]
  },
  '.MuiFormControlLabel-label': {
    color: checked
      ? disabled
        ? `${theme.palette.primary[200]} !important`
        : theme.palette.primary.main
      : theme.palette.grey[600]
  },
  '.MuiRadio-root': {
    display: 'none'
  }
}));

const StyledRadioGroup = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked, disabled }) => ({
  border: `1px solid ${
    checked
      ? disabled
        ? theme.palette.primary[200]
        : theme.palette.primary.main
      : theme.palette.grey[400]
  }`,
  '& .MuiTypography-root': {
    color: checked
      ? disabled
        ? `${theme.palette.primary[200]} !important`
        : theme.palette.primary.main
      : disabled
        ? theme.palette.grey[400]
        : theme.palette.grey[600]
  },
  marginLeft: 0,
  marginRight: 0,
  '& .MuiSvgIcon-root': { fontSize: 28 },
  '.MuiRadio-root': {
    color: checked
      ? disabled
        ? `${theme.palette.primary[200]} !important`
        : theme.palette.primary.main
      : theme.palette.grey[400]
  }
}));

const StyledGroup = (props: StyledGroupProps): ReactElement => {
  const radioGroup = useRadioGroup();
  const { groupType, ...componentProps } = props;

  let checked = false;
  let disabled = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
    disabled = radioGroup.value === props.value;
  }

  return groupType === GroupType.RADIO ? (
    <StyledRadioGroup
      checked={checked}
      disabled={disabled}
      {...componentProps}
    />
  ) : (
    <StyledTableGroup
      checked={checked}
      disabled={disabled}
      {...componentProps}
    />
  );
};

// ==============================|| FORM CONTROL RADIO ||============================== //

const FormControlRadio = ({
  errorMessage,
  helperText,
  captionLabel,
  name,
  value,
  onChange,
  options,
  disabled = false
}: FormControlRadioProps) => {
  const { borderRadius } = useConfig();
  const { t } = useTranslation();
  const theme = useTheme();
  const operationInProgress = useAppSelector(selectAjaxLoader);

  const [hasCaption, setHasCaption] = useState<boolean>(false);
  useEffect(() => {
    setHasCaption(
      options.some(
        (option) => Array.isArray(option.caption) && !!option.caption.length
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const errorState = !!errorMessage?.length;

  return (
    <MUIFormControl fullWidth error={errorState}>
      <FormControl>
        <FormLabel component='legend' error={errorState}>
          <Typography
            variant='body1'
            sx={{
              transform: 'translateX(14px)',
              transformOrigin: 'top left',
              marginBottom: 1
            }}
          >
            {t(captionLabel || '')}
          </Typography>
        </FormLabel>
        <RadioGroup
          aria-label={name}
          value={value || ''}
          onChange={(e) => onChange(e)}
          id={name}
          name={name}
          row={hasCaption}
          sx={{ gap: 2 }}
        >
          {options.map((option, index) => {
            const getLabel = () => (
              <Typography component='span'>
                <Typography variant='button'>{t(option.title)}</Typography>
                {Array.isArray(option.caption) ? (
                  <>
                    {option.caption.map((item, i) => (
                      <span key={i}>
                        <br />
                        <Typography
                          variant='caption'
                          color={theme.palette.text.primary}
                        >
                          {t(item)}
                        </Typography>
                      </span>
                    ))}
                  </>
                ) : null}
              </Typography>
            );

            return (
              <StyledGroup
                groupType={hasCaption ? GroupType.TABLE : GroupType.RADIO}
                key={index}
                disabled={
                  disabled || !!option.helperText || operationInProgress
                }
                control={<Radio />}
                labelPlacement={!hasCaption ? 'start' : undefined}
                value={option.value}
                label={
                  option.helperText ? (
                    <Tooltip title={option.helperText}>{getLabel()}</Tooltip>
                  ) : (
                    getLabel()
                  )
                }
                sx={{
                  borderRadius: `${borderRadius}px`
                }}
              />
            );
          })}
        </RadioGroup>
      </FormControl>

      {errorState ? (
        <FormHelperText error id={`${name}-error`}>
          {t(errorMessage)}
        </FormHelperText>
      ) : helperText ? (
        <FormHelperText id={`${name}-reason`}>{helperText}</FormHelperText>
      ) : null}
    </MUIFormControl>
  );
};

export default FormControlRadio;
